/* ===========================================================================
   LMM · Sprachsuche (Voice Search) — Phase 2, 10.08.2026
   ---------------------------------------------------------------------------
   Weg:  Sprache → SpeechRecognition des Browsers → kleine Sprechform-
         Normalisierung → vorhandenes LMM-Suchfeld → InputEvent → vorhandene
         Predictive Search → vorhandenes LMM-Ranking → vorhandene Suchseite.

   Es entsteht KEINE zweite Suchmaschine und KEIN eigenes Voice-Ranking.
   Ab dem InputEvent übernimmt ausschließlich die bestehende Kette.

   Warum ein eigenes InputEvent nötig ist (gemessen am Canary 203799626065):
   assets/predictive-search.js beginnt seinen Suchhandler mit
       search = debounce((event) => { if (!event.inputType) return; … })
   Ein gewöhnliches new Event('input') wird deshalb verworfen — im Browser
   nachgewiesen: 0 Netzwerkanfragen. Mit
       new InputEvent('input', { bubbles:true, composed:true,
                                 inputType:'insertText', data:<text> })
   liefen dieselben 6 Anfragen wie beim Tippen (Suggest + Nachladestufen).

   Warum die Komponente gescopet wird:
   Im DOM existieren ZWEI <predictive-search-component> — die sichtbare
   LMM-Headersuche (input#lmmh-q-…) und die unsichtbare Horizon-Standardsuche
   (input#cmdk-input, 0 × 0 px). Ein globales document.querySelector('input[name=q]')
   träfe je nach Reihenfolge das falsche Feld. Jeder Button spricht deshalb
   ausschließlich das Suchfeld seiner EIGENEN Komponente an (closest()).

   Datenschutztechnik: Die Erkennung leistet der Browser bzw. das Betriebssystem.
   Diese Datei erzeugt kein Audio, keinen Blob, keinen MediaRecorder, speichert
   kein Transkript, setzt kein Cookie, ruft keinen externen Dienst und sendet
   nichts an einen LMM-Server. Kein GA4-Event, kein Pixel, keine Persistenz.

   Warum Einheiten geklebt werden (am 10.08.2026 gegen die echte native
   Shopify-Suche des Canary gemessen, Chris-Befund „Sprache ≠ Tippen"):
   Die Suche zerlegt die Anfrage in Tokens. „18W" ist EIN Token und trifft den
   Titel-Token „18W/840". „18 W" zerfällt in „18" und „W" — und ein einzelner
   Buchstabe ist ein eigener, sehr schwacher Treffer: „Leuchtstoffröhre w"
   liefert dieselben warmweiß-Produkte wie „Leuchtstoffröhre k" die kaltweißen.
   Gemessene Folge: „Leuchtstoffröhre 18 W" liefert exakt denselben Pool wie
   „Leuchtstoffröhre 18" (die Einheit trägt nichts bei), „Philips TUV 11 W" und
   „Osram 18 W 840" liefern 0 native Treffer, „… 11W" bzw. „… 18W 840" dagegen
   3 bzw. 10. Ganzzahl-Gegenprobe über 5/6/8/11/13/16/18/24/36/58 W: geklebt
   9–10 Titeltreffer je 10, mit Leerzeichen 0–2.

   Umgekehrt bei Nachkommawerten: Titel schreiben Bereiche wie „2,6-30W/840",
   der eigenständige Token ist dort die blanke Zahl. Gemessen über
   2,6 / 2,9 / 3,5 / 4,9 / 5,5 / 7,3 / 8,5 / 4,3 / 6,5: „2,6W" 0 Treffer,
   „2,6 W" 10. Deshalb: Ganzzahl geklebt, Nachkommawert mit Leerzeichen —
   beides mit Einheit, damit die bestehende LMM-Wattlogik sie weiter liest.

   Kein automatisches Absenden: nach dem Erkennen steht der Text im normalen
   Suchfeld, das Dropdown öffnet sich, der Kunde entscheidet selbst.
   =========================================================================== */
(function () {
  'use strict';

  /* Guard gegen Doppelinitialisierung (Section-Rerender, PJAX, Mehrfacheinbindung). */
  if (window.__lmmVoiceSearch) return;

  var BTN = '.lmmv-btn';
  var STATUS = '.lmmv-status';
  var RESET = '.lmm-predictive__reset, [ref="resetButton"]';

  /* Feature Detection — ausschließlich API-Prüfung, keine User-Agent-Erkennung. */
  var SR = window.SpeechRecognition || window.webkitSpeechRecognition;

  /* ======================================================================
     1 · Sprechform-Normalisierung (läuft NUR zwischen Transkript und Feld)
     ----------------------------------------------------------------------
     Sie ersetzt die bestehende LMM-Suchnormalisierung nicht und wird auf
     getippte Eingaben niemals angewandt.
     ====================================================================== */

  var GRUND = {
    'null': 0, 'eins': 1, 'ein': 1, 'eine': 1, 'zwei': 2, 'zwo': 2, 'drei': 3,
    'vier': 4, 'fuenf': 5, 'sechs': 6, 'sieben': 7, 'acht': 8, 'neun': 9,
    'zehn': 10, 'elf': 11, 'zwoelf': 12, 'dreizehn': 13, 'vierzehn': 14,
    'fuenfzehn': 15, 'sechzehn': 16, 'siebzehn': 17, 'achtzehn': 18, 'neunzehn': 19
  };
  var ZEHNER = {
    'zwanzig': 20, 'dreissig': 30, 'vierzig': 40, 'fuenfzig': 50,
    'sechzig': 60, 'siebzig': 70, 'achtzig': 80, 'neunzig': 90
  };
  /* Nur diese Einheiten — im unmittelbaren Anschluss an eine Zahl. */
  var EINHEIT = { 'watt': 'W', 'kelvin': 'K', 'lumen': 'lm' };
  /* Getrennt gesprochene Vielfache. Die Spracherkennung liefert Zusammen-
     setzungen mal als ein Wort („viertausend"), mal als zwei („vier tausend"). */
  var VIELFACH = { 'hundert': 100, 'tausend': 1000 };
  /* Bekannte Sockelpräfixe mit ihren real existierenden Nummern.
     Keine unbekannte Kombination wird erfunden: passt die Zahl nicht in die
     Liste, bleibt der gesprochene Text unverändert stehen. */
  var SOCKEL = {
    'G': ['4', '5', '6.35', '8', '8.5', '9', '10', '12', '13', '23', '24', '53'],
    'GU': ['4', '5.3', '10'],
    'E': ['10', '14', '27', '40'],
    'R': ['7'],
    'T': ['2', '4', '5', '8', '9', '10', '12', '16'],
    '2G': ['7', '10', '11']
  };
  /* Serienkürzel: nur eindeutig buchstabierte Folgen. */
  var SERIE = {
    'p l s': 'PL-S', 'p l l': 'PL-L', 'p l c': 'PL-C', 'p l t': 'PL-T', 't l d': 'TL-D',
    'pl s': 'PL-S', 'pl l': 'PL-L', 'pl c': 'PL-C', 'pl t': 'PL-T', 'tl d': 'TL-D'
  };
  /* Farbcodes: bewusst nur die vier ausdrücklich benannten Werte. */
  var FARBCODE = { '827': 1, '830': 1, '840': 1, '865': 1 };
  /* Ausdrücklich gesprochene Identifikator-Präfixe. */
  var IDENT = { 'ean': 1, 'gtin': 1, 'sku': 1, 'artikelnummer': 1, 'artikelnr': 1 };
  /* Starke Signale für den Lampenkontext (Voraussetzung für Farbcodes). */
  var KONTEXT = /(?:^|\s)(?:\d+(?:[.,]\d+)?\s?(?:W|K|lm)|(?:2G|GU|G|E|R|T)\d|PL-[SLCT]|TL-D)(?:\s|$)/;
  var KONTEXTWORT = /\b(?:led|roehre|roehren|röhre|röhren|leuchtstoff\w*|leuchtmittel|lampe|lampen|tube|birne|strahler|spot|halogen|kompaktleuchtstoff\w*|neon\w*)\b/i;

  /** Umlaute vereinheitlichen — die Zahlwörter stehen ohne Umlaute im Katalog. */
  function flach(w) {
    return String(w).toLowerCase()
      .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss');
  }

  /**
   * Ein einzelnes deutsches Zahlwort → Zahl, sonst null.
   * Trägt sich rekursiv durch „…tausend…", „…hundert…" und „…und…".
   * Null propagiert: unbekannte Wortteile machen das ganze Wort ungültig,
   * damit aus „hundertprozentig" oder „Sekunde" niemals eine Zahl wird.
   */
  function zahlWort(w) {
    if (!w) return null;
    if (Object.prototype.hasOwnProperty.call(GRUND, w)) return GRUND[w];
    if (Object.prototype.hasOwnProperty.call(ZEHNER, w)) return ZEHNER[w];
    var i = w.indexOf('tausend');
    if (i !== -1) {
      var a = i === 0 ? 1 : zahlWort(w.slice(0, i));
      var b = w.length === i + 7 ? 0 : zahlWort(w.slice(i + 7));
      return a === null || b === null ? null : a * 1000 + b;
    }
    i = w.indexOf('hundert');
    if (i !== -1) {
      var c = i === 0 ? 1 : zahlWort(w.slice(0, i));
      var d = w.length === i + 7 ? 0 : zahlWort(w.slice(i + 7));
      return c === null || d === null ? null : c * 100 + d;
    }
    i = w.indexOf('und');
    if (i > 0) {
      var e = zahlWort(w.slice(0, i));
      var f = zahlWort(w.slice(i + 3));
      if (e === null || f === null) return null;
      if (e < 10 && f >= 20 && f <= 90 && f % 10 === 0) return e + f;
      return null;
    }
    return null;
  }

  /**
   * Zahl ab Position i lesen — als Ziffernfolge oder als Zahlwort, optional mit
   * „Komma" und genau einer Nachkommastelle bzw. einer bereits erkannten
   * Ziffernfolge. Lässt sich der Nachkommateil NICHT sicher lesen, wird gar
   * nichts umgewandelt (sonst ginge „zwei Komma fünfundzwanzig" still zu „2").
   * @param {string} dez Dezimalzeichen: ',' technisch, '.' für Sockel wie GU5.3
   */
  function zahlAb(toks, i, dez) {
    var t = toks[i];
    if (t === undefined) return null;
    var wert = null;
    var m = /^(\d+)(?:[.,](\d+))?$/.exec(t);
    if (m) wert = m[2] === undefined ? m[1] : m[1] + dez + m[2];
    else {
      var n = zahlWort(flach(t));
      if (n === null) return null;
      wert = String(n);
    }
    var j = i + 1;
    /* „vier tausend Kelvin" → 4000 K. Nur unmittelbar hinter einer gelesenen
       Zahl und nur diese beiden Wörter. Das Ergebnis wird ohnehin erst
       übernommen, wenn danach eine Einheit oder ein bekannter Sockel steht —
       „vier tausend Euro" bleibt deshalb unangetastet. */
    if (wert.indexOf(dez) === -1 && toks[j] !== undefined && VIELFACH[flach(toks[j])]) {
      wert = String(parseInt(wert, 10) * VIELFACH[flach(toks[j])]);
      j += 1;
    }
    if (wert.indexOf(dez) === -1 && toks[j] !== undefined && flach(toks[j]) === 'komma') {
      var nach = toks[j + 1];
      var teil = null;
      if (nach !== undefined && /^\d+$/.test(nach)) teil = nach;
      else if (nach !== undefined) {
        var q = zahlWort(flach(nach));
        if (q !== null && q >= 0 && q <= 9) teil = String(q);
      }
      if (teil === null) return null; /* Unsicher → gar nicht umwandeln. */
      wert += dez + teil;
      j += 2;
    }
    return { wert: wert, ende: j };
  }

  /**
   * Kanonische Schreibweise für Zahl + technische Einheit.
   * Ganzzahl geklebt („18W", „4000K", „850lm"), Nachkommawert mit Leerzeichen
   * („2,6 W"). Begründung und Messreihe stehen im Kopfkommentar dieser Datei.
   * Die Einheit bleibt in beiden Fällen erhalten, damit die bestehende
   * LMM-Wattlogik (RE_QWATT in snippets/lmm-tech-rank.liquid) sie weiter liest.
   * @param {string} wert
   * @param {string} einheit
   * @returns {string}
   */
  function mitEinheit(wert, einheit) {
    return wert.indexOf(',') === -1 ? wert + einheit : wert + ' ' + einheit;
  }

  /** Reines Zahlwort (keine Ziffernfolge) → Zahl, sonst null. */
  function nurWortZahl(t) {
    if (t === undefined || /\d/.test(t)) return null;
    return zahlWort(flach(t));
  }

  /**
   * Identifikatoren: NUR einzeln gesprochene Ziffern bzw. bereits gesprochene
   * Ziffernfolgen werden verlustfrei zusammengezogen. Keine Fuzzy-Korrektur,
   * keine Zerlegung von „vierhundertfünf". Taucht im Anschluss ein mehrstelliges
   * Zahlwort auf, bleibt der Originaltext vollständig stehen.
   */
  function identifikator(toks, i) {
    var j = i + 1;
    var ziffern = [];
    while (j < toks.length) {
      var t = toks[j];
      if (/^\d+$/.test(t)) { ziffern.push(t); j += 1; continue; }
      var z = nurWortZahl(t);
      if (z !== null && z >= 0 && z <= 9) { ziffern.push(String(z)); j += 1; continue; }
      break;
    }
    if (!ziffern.length) return null;
    var stopp = nurWortZahl(toks[j]);
    if (stopp !== null) return null; /* z. B. „vierhundertfünf" → nichts anfassen. */
    return { wert: ziffern.join(''), ende: j };
  }

  /**
   * Rohtranskript → Voice-Schreibform.
   * @param {string} roh
   * @returns {string}
   */
  function normalisiere(roh) {
    var text = String(roh == null ? '' : roh)
      .replace(/[!?;:]+/g, ' ')
      .replace(/[.,](?!\d)/g, ' ')          /* Satzzeichen weg, „2,6" und „5.3" bleiben */
      .replace(/\s+/g, ' ')
      .trim();
    if (!text) return '';

    var toks = text.split(' ');
    var out = [];
    var i = 0;
    while (i < toks.length) {
      var roh1 = toks[i];
      var t1 = flach(roh1);
      var t2 = toks[i + 1] !== undefined ? flach(toks[i + 1]) : '';
      var t3 = toks[i + 2] !== undefined ? flach(toks[i + 2]) : '';
      var r;

      /* 0 · Steht unmittelbar davor ein „Komma", gehört dieses Token zu einer
         Nachkommastelle, die sich nicht sicher lesen ließ (zahlAb hätte sie sonst
         mitgenommen). Dann wird hier NICHTS umgewandelt — sonst entstünde aus
         „zwei Komma fünfundzwanzig Watt" das falsche „zwei Komma 25 W". */
      if (i > 0 && flach(toks[i - 1]) === 'komma') { out.push(roh1); i += 1; continue; }

      /* 1 · Identifikator nach ausdrücklichem Präfix */
      if (IDENT[t1] || (t1 === 'artikel' && (t2 === 'nummer' || t2 === 'nr'))) {
        var start = IDENT[t1] ? i : i + 1;
        r = identifikator(toks, start);
        if (r) { out.push(r.wert); i = r.ende; continue; }
      }

      /* 2 · „zwei G elf" → 2G11 (vor der allgemeinen Sockelregel) */
      if ((t1 === 'zwei' || t1 === 'zwo' || t1 === '2') && t2 === 'g') {
        r = zahlAb(toks, i + 2, '.');
        if (r && SOCKEL['2G'].indexOf(r.wert) !== -1) {
          out.push('2G' + r.wert);
          i = r.ende;
          continue;
        }
      }

      /* 3 · Serienkürzel (buchstabiert) */
      if (SERIE[t1 + ' ' + t2 + ' ' + t3]) { out.push(SERIE[t1 + ' ' + t2 + ' ' + t3]); i += 3; continue; }
      if (SERIE[t1 + ' ' + t2]) { out.push(SERIE[t1 + ' ' + t2]); i += 2; continue; }

      /* 4 · Sockel: bekanntes Präfix + bekannte Nummer */
      var praefix = null;
      var ab = i + 1;
      if (t1 === 'g' && t2 === 'u') { praefix = 'GU'; ab = i + 2; }
      else if (t1 === 'gu' || t1 === 'g' || t1 === 'e' || t1 === 'r' || t1 === 't') praefix = t1.toUpperCase();
      if (praefix) {
        r = zahlAb(toks, ab, '.');
        if (r && SOCKEL[praefix] && SOCKEL[praefix].indexOf(r.wert) !== -1) {
          var sockel = praefix + r.wert;
          var ende = r.ende;
          /* „R sieben s" → R7s */
          if (praefix === 'R' && toks[ende] !== undefined && flach(toks[ende]) === 's') {
            sockel += 's';
            ende += 1;
          }
          out.push(sockel);
          i = ende;
          continue;
        }
      }

      /* 5 · Zahl + Einheit (nur im unmittelbaren Einheitenkontext) */
      r = zahlAb(toks, i, ',');
      if (r && toks[r.ende] !== undefined && EINHEIT[flach(toks[r.ende])]) {
        out.push(mitEinheit(r.wert, EINHEIT[flach(toks[r.ende])]));
        i = r.ende + 1;
        continue;
      }

      out.push(roh1);
      i += 1;
    }

    /* 6 · Farbcodes — nur bei belegtem technischem Lampenkontext. */
    var zwischen = out.join(' ');
    if (KONTEXT.test(' ' + zwischen + ' ') || KONTEXTWORT.test(zwischen)) {
      var end = [];
      var k = 0;
      while (k < out.length) {
        var eins = nurWortZahl(out[k]);
        if (eins !== null && FARBCODE[String(eins)]) { end.push(String(eins)); k += 1; continue; }
        var zwei = nurWortZahl(out[k + 1]);
        if (eins !== null && eins >= 0 && eins <= 9 && zwei !== null && zwei >= 10 && zwei <= 99) {
          var code = String(eins * 100 + zwei);
          if (FARBCODE[code]) { end.push(code); k += 2; continue; }
        }
        end.push(out[k]);
        k += 1;
      }
      out = end;
    }

    var fertig = out.join(' ').replace(/\s+/g, ' ').trim();
    /* Dasselbe Limit wie im bestehenden Suchbegriff-Gedächtnis (255). */
    if (fertig.length > 255) {
      fertig = fertig.slice(0, 255);
      if (/[\uD800-\uDBFF]$/.test(fertig)) fertig = fertig.slice(0, -1);
    }
    return fertig;
  }

  /* Ohne SpeechRecognition endet die Datei hier: der Button bleibt serverseitig
     `hidden`, es gibt keine tote Schaltfläche, keinen zusätzlichen Request und
     die normale Suche bleibt in jedem Detail unverändert. */
  window.__lmmVoiceSearch = {
    version: '2026.08.10-3',
    verfuegbar: !!SR,
    normalisiere: normalisiere
  };
  if (!SR) return;

  /* ======================================================================
     2 · Zustände und Meldungen
     ====================================================================== */

  var TEXTE = {
    requesting: { t: 'Mikrofon wird angefragt …', ton: 'warte', dauer: 0 },
    listening: { t: 'Ich höre zu …', ton: 'hoert', dauer: 0 },
    nospeech: { t: 'Ich habe nichts verstanden. Bitte versuche es noch einmal.', ton: 'fehler', dauer: 6000 },
    denied: { t: 'Kein Zugriff auf das Mikrofon. Bitte die Freigabe im Browser erlauben.', ton: 'fehler', dauer: 8000 },
    blocked: { t: 'Die Spracherkennung ist in diesem Browser gesperrt.', ton: 'fehler', dauer: 8000 },
    capture: { t: 'Kein Mikrofon gefunden. Bitte Gerät und Einstellungen prüfen.', ton: 'fehler', dauer: 8000 },
    network: { t: 'Die Spracherkennung ist gerade nicht erreichbar. Bitte später erneut versuchen.', ton: 'fehler', dauer: 8000 },
    sprache: { t: 'Deutsch wird von der Spracherkennung dieses Browsers nicht unterstützt.', ton: 'fehler', dauer: 8000 },
    timeout: { t: 'Sprachsuche ist in diesem Browser derzeit nicht verfügbar.', ton: 'fehler', dauer: 8000 },
    fehler: { t: 'Die Spracherkennung wurde unterbrochen. Bitte erneut versuchen.', ton: 'fehler', dauer: 6000 }
  };
  var FEHLER = {
    'no-speech': 'nospeech',
    'not-allowed': 'denied',
    'service-not-allowed': 'blocked',
    'audio-capture': 'capture',
    'network': 'network',
    'language-not-supported': 'sprache',
    'phrases-not-supported': 'fehler',
    'bad-grammar': 'fehler'
  };

  var START_FRIST = 7000;   /* kein start/result/error → Browser bietet die API nur nominell an */
  var MAX_DAUER = 20000;    /* harte Obergrenze je Vorgang, keine Dauererkennung */
  var ZEIGE_FINAL = 2500;

  var aktiv = null;         /* genau ein Vorgang gleichzeitig */
  var eigenesEvent = false; /* markiert das selbst ausgeloeste InputEvent */
  var debug = false;
  try {
    debug = new URLSearchParams(location.search).get('lmm_voice_debug') === '1';
  } catch (e) { /* ohne URLSearchParams bleibt der Debugmodus aus */ }

  function komponente(el) {
    return el ? el.closest('predictive-search-component') : null;
  }
  /** Immer nur das Suchfeld der EIGENEN sichtbaren Komponente. */
  function feldZu(btn) {
    var comp = komponente(btn);
    return comp ? comp.querySelector('input[name="q"]') : null;
  }
  function statusZu(btn) {
    var comp = komponente(btn);
    return comp ? comp.querySelector(STATUS) : null;
  }

  function melde(btn, text, ton, dauer) {
    var el = statusZu(btn);
    if (!el) return;
    if (el.__lmmvTimer) { clearTimeout(el.__lmmvTimer); el.__lmmvTimer = null; }
    if (!text) {
      el.textContent = '';
      el.removeAttribute('data-lmmv-ton');
      return;
    }
    if (el.textContent !== text) el.textContent = text;
    el.setAttribute('data-lmmv-ton', ton || 'warte');
    if (dauer) {
      el.__lmmvTimer = setTimeout(function () {
        el.__lmmvTimer = null;
        el.textContent = '';
        el.removeAttribute('data-lmmv-ton');
      }, dauer);
    }
  }
  function meldeZustand(btn, schluessel) {
    var z = TEXTE[schluessel] || TEXTE.fehler;
    melde(btn, z.t, z.ton, z.dauer);
  }

  function knopf(btn, zustand) {
    if (!btn) return;
    if (zustand) btn.setAttribute('data-lmmv-state', zustand);
    else btn.removeAttribute('data-lmmv-state');
    /* Auch waehrend „requesting" beendet ein Druck den Vorgang — Beschriftung und
       aria-pressed muessen sich deshalb gemeinsam bewegen, sonst meldet der
       Screenreader „gedrueckt" und gleichzeitig „Sprachsuche starten". */
    var hoert = zustand === 'listening' || zustand === 'requesting';
    btn.setAttribute('aria-pressed', hoert ? 'true' : 'false');
    var label = hoert ? 'Spracheingabe beenden' : 'Sprachsuche starten';
    btn.setAttribute('aria-label', label);
    btn.setAttribute('title', label);
  }

  /* ======================================================================
     3 · Einspeisung in die bestehende Suche
     ====================================================================== */

  function einspeisen(input, text) {
    input.value = text;
    input.focus();
    try { input.setSelectionRange(text.length, text.length); } catch (e) { /* type=search */ }
    eigenesEvent = true;
    try {
      input.dispatchEvent(new InputEvent('input', {
        bubbles: true,
        composed: true,
        inputType: 'insertText',
        data: text
      }));
    } finally {
      eigenesEvent = false;
    }
  }

  /* ======================================================================
     4 · Vorgangssteuerung
     ====================================================================== */

  function aufraeumen(s) {
    if (!s) return;
    s.tot = true;
    clearTimeout(s.startWacht);
    clearTimeout(s.maxWacht);
    if (s.rec) {
      s.rec.onstart = null;
      s.rec.onresult = null;
      s.rec.onerror = null;
      s.rec.onend = null;
      s.rec.onnomatch = null;
      s.rec.onaudiostart = null;
    }
    if (aktiv === s) aktiv = null;
    knopf(s.btn, null);
  }

  /** Bricht sauber ab; ein spät eintreffendes Ergebnis wird verworfen. */
  function verwerfe(grund) {
    var s = aktiv;
    if (!s) return;
    var rec = s.rec;
    var btn = s.btn;
    aufraeumen(s);
    if (rec) { try { rec.abort(); } catch (e) { /* bereits beendet */ } }
    melde(btn, '', null, 0);
  }

  /**
   * Verwertet ein fertiges Transkript. Der Vorgang ist zu diesem Zeitpunkt
   * bereits abgeräumt — deshalb arbeitet die Funktion nur noch mit Knopf und
   * Feld und kann von jedem Ausstiegspfad aus aufgerufen werden.
   */
  function verwerte(btn, input, roh) {
    var text = normalisiere(roh);
    if (debug) {
      melde(btn, 'Roh: „' + String(roh).trim() + '“ · Normalisiert: „' + (text || '—')
        + '“ · Suche: „' + (text || '—') + '“', 'warte', 12000);
    }
    if (!text) {
      /* Kein verwertbarer Text: Feld bleibt unangetastet, kein InputEvent. */
      if (!debug) meldeZustand(btn, 'nospeech');
      return;
    }
    if (text === input.value) {
      /* Unveränderter Begriff: keine zweite Suchanfrage erzwingen, Fokus und
         vorhandene Ergebnisse bleiben erhalten. */
      input.focus();
      try { input.setSelectionRange(text.length, text.length); } catch (e) { /* type=search */ }
      if (!debug) melde(btn, 'Suchbegriff unverändert: „' + text + '“', 'warte', ZEIGE_FINAL);
      return;
    }
    einspeisen(input, text);
    if (!debug) melde(btn, 'Suche nach „' + text + '“', 'warte', ZEIGE_FINAL);
  }

  /** Vorgang beenden und das Transkript verwerten (genau ein Weg dorthin). */
  function schliesseAb(s) {
    if (s.tot) return;
    var rec = s.rec;
    var btn = s.btn;
    var input = s.input;
    var roh = s.final;
    aufraeumen(s);
    if (rec) { try { rec.abort(); } catch (e) { /* bereits beendet */ } }
    if (roh.trim()) verwerte(btn, input, roh);
    else meldeZustand(btn, 'nospeech');
  }

  function starte(btn) {
    var input = feldZu(btn);
    if (!input) return;

    var s = { btn: btn, input: input, rec: null, tot: false, final: '', grund: null };
    aktiv = s;
    knopf(btn, 'requesting');
    meldeZustand(btn, 'requesting');

    try {
      s.rec = new SR();
    } catch (e) {
      aufraeumen(s);
      meldeZustand(btn, 'timeout');
      return;
    }
    try {
      s.rec.lang = 'de-DE';
      s.rec.continuous = false;      /* keine Dauererkennung, kein Neustart */
      s.rec.interimResults = true;
      s.rec.maxAlternatives = 3;
    } catch (e) { /* nicht unterstützte Eigenschaften bleiben auf ihrem Standard */ }

    s.rec.onstart = function () {
      if (s.tot) return;
      clearTimeout(s.startWacht);
      knopf(btn, 'listening');
      meldeZustand(btn, 'listening');
    };
    s.rec.onresult = function (ev) {
      if (s.tot) return;
      clearTimeout(s.startWacht);
      var zwischen = '';
      for (var i = ev.resultIndex; i < ev.results.length; i += 1) {
        var r = ev.results[i];
        if (r.isFinal) s.final += r[0].transcript;
        else zwischen += r[0].transcript;
      }
      if (s.final.trim()) { schliesseAb(s); return; }
      /* Zwischenergebnis: nur Statusanzeige, NIE ins echte Suchfeld. */
      if (zwischen.trim()) melde(btn, 'Ich höre zu … „' + zwischen.trim() + '“', 'hoert', 0);
    };
    s.rec.onerror = function (ev) {
      if (s.tot) return;
      var code = ev && ev.error ? ev.error : '';
      /* Ein Fehler nach einem bereits gelieferten Endergebnis darf dieses nicht
         entwerten — dann zählt das Transkript. */
      if (s.final.trim()) { schliesseAb(s); return; }
      aufraeumen(s);
      if (code === 'aborted') { melde(btn, '', null, 0); return; }
      meldeZustand(btn, FEHLER[code] || 'fehler');
    };
    s.rec.onend = function () {
      /* Beendet ohne Ergebnis und ohne Fehler → nichts verstanden. */
      schliesseAb(s);
    };

    s.startWacht = setTimeout(function () {
      if (s.tot) return;
      /* Weder start noch result noch error: die API existiert nur nominell. */
      var rec = s.rec;
      aufraeumen(s);
      if (rec) { try { rec.abort(); } catch (e) { /* egal */ } }
      meldeZustand(btn, 'timeout');
    }, START_FRIST);
    s.maxWacht = setTimeout(function () {
      /* Harte Obergrenze je Vorgang — keine Endloserkennung. */
      schliesseAb(s);
    }, MAX_DAUER);

    try {
      s.rec.start();
    } catch (e) {
      var rec2 = s.rec;
      aufraeumen(s);
      if (rec2) { try { rec2.abort(); } catch (e2) { /* egal */ } }
      meldeZustand(btn, 'timeout');
    }
  }

  /** Klick auf das Mikrofon: startet oder beendet — nie beides gleichzeitig. */
  function schalte(btn) {
    if (aktiv) {
      var lief = aktiv.btn === btn;
      verwerfe('knopf');
      if (lief) return;   /* zweiter Klick auf denselben Knopf = beenden */
    }
    starte(btn);
  }

  /* ======================================================================
     5 · Aktivierung und Ereignisse
     ----------------------------------------------------------------------
     Alle Listener liegen genau einmal am WINDOW in der Capture-Phase.
     Section-Rerender oder mehrfache Nutzung erzeugen dadurch weder doppelte
     Listener noch Speicherlecks.

     Warum window und nicht document (am 10.08.2026 im Browser gemessen):
     snippets/lmm-search-term-keep.liquid haengt einen keydown-Listener in der
     Capture-Phase an DOCUMENT und ruft bei GEFUELLTEM Suchfeld
     stopImmediatePropagation() auf — damit Escape die Liste schliesst, ohne den
     Suchbegriff zu verlieren. Alle spaeter registrierten document-Listener
     kommen dann nicht mehr dran; ein Escape-Abbruch der Sprachaufnahme waere
     also genau im Regelfall wirkungslos geblieben. Der Capture-Pfad beginnt am
     window, deshalb laeuft dieser Listener davor. Er stoppt selbst nichts —
     das bestehende Verhalten bleibt in jedem Detail unveraendert.
     ====================================================================== */

  function aktiviere() {
    var liste = document.querySelectorAll(BTN + '[hidden]');
    for (var i = 0; i < liste.length; i += 1) {
      if (!komponente(liste[i])) continue;   /* nur innerhalb einer echten Suchkomponente */
      liste[i].hidden = false;
      knopf(liste[i], null);
    }
  }

  window.addEventListener('pointerdown', function (ev) {
    var btn = ev.target instanceof Element ? ev.target.closest(BTN) : null;
    if (!btn) return;
    /* Liegt der Fokus bereits im Suchfeld, bleibt er dort — der Cursor springt
       nicht weg. Bewusst nur für die Maus: auf Touch kann ein verhindertes
       pointerdown den nachfolgenden Klick unterdrücken. Tastaturnutzung ist
       ohnehin nicht betroffen, dort entsteht kein pointerdown. Das Dropdown
       bleibt in beiden Fällen offen, weil der Knopf INNERHALB der Komponente
       liegt, an der :focus-within hängt. */
    if (ev.pointerType === 'mouse' && document.activeElement === feldZu(btn)) ev.preventDefault();
  }, true);

  window.addEventListener('click', function (ev) {
    var ziel = ev.target instanceof Element ? ev.target : null;
    if (!ziel) return;
    var btn = ziel.closest(BTN);
    if (btn) { schalte(btn); return; }
    if (aktiv && ziel.closest(RESET)) verwerfe('reset');
  }, true);

  window.addEventListener('keydown', function (ev) {
    if (ev.key === 'Escape' && aktiv) verwerfe('escape');
  }, true);

  /* Tippt der Kunde selbst, gewinnt seine Eingabe: die Erkennung wird beendet
     und ein spätes Ergebnis darf den korrigierten Text nicht überschreiben. */
  window.addEventListener('input', function (ev) {
    if (eigenesEvent || !aktiv) return;
    if (ev.target === aktiv.input) verwerfe('manuell');
  }, true);

  aktiviere();
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', aktiviere);
  window.addEventListener('pageshow', aktiviere);
  document.addEventListener('shopify:section:load', aktiviere);
})();
